/** §2.7 VIN-level slice of the outreach basket.
 *
 *  The basket persists buyer-first, but the underlying relationship is many-to-many
 *  (a VIN can be staged against several buyers). This is the same data re-pivoted,
 *  exactly as `toSellerGroups` does for the seller slice — no storage change.
 *
 *  Lives in its own module rather than in BuyerBasket.tsx because a `.tsx` component
 *  file may only export components (`react-refresh/only-export-components`), and this
 *  needs to be importable by tests.
 */
import type { BasketBuyer, BasketBuyerInfo } from './BuyerBasket';

/** One staged VIN and every buyer it is currently pitched to. */
export interface BasketVinGroup {
  vin: string;
  label: string;
  seller: string | null;
  sellerId: string | null;
  sellerLoc: string | null;
  buyers: BasketBuyerInfo[];
}

/** Re-pivot the buyer-grouped basket into one row per VIN (client-side only). */
export function toVinGroups(buyers: BasketBuyer[]): BasketVinGroup[] {
  const map = new Map<string, BasketVinGroup>();
  for (const { buyer, vins } of buyers) {
    for (const v of vins) {
      let g = map.get(v.vin);
      if (!g) {
        g = {
          vin: v.vin,
          label: v.label,
          seller: v.seller ?? null,
          sellerId: v.seller_id ?? null,
          sellerLoc: v.seller_loc ?? null,
          buyers: [],
        };
        map.set(v.vin, g);
      }
      // The same buyer cannot be pitched one VIN twice, but the basket is user
      // state restored from storage, so do not assume it.
      if (!g.buyers.some((b) => b.buyer_uid === buyer.buyer_uid)) {
        g.buyers.push(buyer);
      }
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => (a.label || a.vin).localeCompare(b.label || b.vin) || a.vin.localeCompare(b.vin),
  );
}

/** A buyer staged against several VINs, and which ones. */
export interface BuyerOverlap {
  buyer: BasketBuyerInfo;
  vins: string[];
}

/**
 * Buyers staged against more than one of `groups`.
 *
 * §2.0: the per-VIN bulk email sends one message per VIN, so a buyer matched to two
 * selected VINs receives two emails. With 2.9 (contact log) dropped, nothing else
 * tracks that — surfacing the overlap before the broker copies is what makes the
 * duplication a choice rather than a surprise.
 */
export function overlappingBuyers(groups: BasketVinGroup[]): BuyerOverlap[] {
  const seen = new Map<string, BuyerOverlap>();
  for (const g of groups) {
    for (const b of g.buyers) {
      const hit = seen.get(b.buyer_uid);
      if (hit) hit.vins.push(g.vin);
      else seen.set(b.buyer_uid, { buyer: b, vins: [g.vin] });
    }
  }
  return Array.from(seen.values())
    .filter((x) => x.vins.length > 1)
    .sort((a, b) => b.vins.length - a.vins.length);
}

/** One generic email: a set of VINs and the buyers it goes to. */
export interface OutreachGroup {
  key: string;
  vins: BasketVinGroup[];
  buyers: BasketBuyerInfo[];
}

/**
 * §2.1 — partition the basket into one generic email per group of buyers who are
 * staged against exactly the same VINs.
 *
 * The partition is over BUYERS, keyed by their staged VIN set. Each buyer has one
 * such set, so each buyer appears in exactly one email; and because the set is the
 * buyer's own, every recipient is staged against every VIN the email lists. Those
 * are the two rules the broker gave, and this satisfies both without compromise.
 *
 * A VIN may appear in more than one email — VIN A goes to the buyers staged only
 * against A, and again to those staged against A and C. That is correct: they are
 * different recipients, and no buyer is contacted twice.
 */
export function toOutreachGroups(buyers: BasketBuyer[]): OutreachGroup[] {
  const vinGroups = toVinGroups(buyers);
  const vinByCode = new Map(vinGroups.map((g) => [g.vin, g]));

  // Collapse to one VIN set per buyer first: the basket is restored user state, so
  // one buyer arriving as two entries must not become two emails.
  const byBuyer = new Map<string, { buyer: BasketBuyerInfo; vins: Set<string> }>();
  for (const g of vinGroups) {
    for (const b of g.buyers) {
      const hit = byBuyer.get(b.buyer_uid);
      if (hit) hit.vins.add(g.vin);
      else byBuyer.set(b.buyer_uid, { buyer: b, vins: new Set([g.vin]) });
    }
  }

  const bySet = new Map<string, OutreachGroup>();
  for (const { buyer, vins } of byBuyer.values()) {
    // Sorted so the key depends only on set membership, not on the order the VINs
    // happened to be collected in.
    const codes = Array.from(vins).sort();
    const key = codes.join('|');
    const hit = bySet.get(key);
    if (hit) {
      hit.buyers.push(buyer);
      continue;
    }
    bySet.set(key, {
      key,
      vins: codes
        .map((c) => vinByCode.get(c))
        .filter((g): g is BasketVinGroup => !!g)
        .sort((a, b) => (a.label || a.vin).localeCompare(b.label || b.vin)),
      buyers: [buyer],
    });
  }

  for (const g of bySet.values()) {
    g.buyers.sort((a, b) =>
      (a.buyer_name || a.buyer_uid).localeCompare(b.buyer_name || b.buyer_uid));
  }
  return Array.from(bySet.values()).sort((a, b) => b.buyers.length - a.buyers.length
    || (a.vins[0]?.label ?? '').localeCompare(b.vins[0]?.label ?? ''));
}
